import React, { useState } from "react";
import { Mail, Lock, Loader2, Save } from "lucide-react";
import ProfileSettingsCard from "../common/ProfileSettingsCard";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { useUser } from "../../context/UserContext";

function ChangeEmailCard({ user, changeEmail }) {
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!password) {
      toast.error("Enter your current password to confirm.");
      return;
    }
    setSaving(true);
    const res = await changeEmail({ email: email.trim(), currentPassword: password });
    setSaving(false);
    if (res?.ok) {
      toast.success("Email address updated!");
      setPassword("");
    } else {
      toast.error(res?.error || "Could not change email.");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="change-email-card">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="h-5 w-5 text-gold-dark" />
        <h2 className="font-serif text-lg font-semibold text-navy">Email Address</h2>
      </div>
      <div className="space-y-3 max-w-md">
        <div>
          <label className="text-sm font-medium text-gray-700">New email</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="mt-1.5"
            data-testid="change-email-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Current password</label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Confirm with your password"
            className="mt-1.5"
            data-testid="change-email-password"
          />
        </div>
        <Button onClick={submit} disabled={saving} className="bg-navy hover:bg-navy-light text-white font-semibold" data-testid="change-email-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Update Email</>}
        </Button>
      </div>
    </div>
  );
}

function ChangePasswordCard({ changePassword }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    const res = await changePassword({ currentPassword: current, newPassword: next });
    setSaving(false);
    if (res?.ok) {
      toast.success("Password changed successfully!");
      setCurrent(""); setNext(""); setConfirm("");
    } else {
      toast.error(res?.error || "Could not change password.");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="h-5 w-5 text-gold-dark" />
        <h2 className="font-serif text-lg font-semibold text-navy">Password</h2>
      </div>
      <div className="space-y-3 max-w-md">
        <div>
          <label className="text-sm font-medium text-gray-700">Current password</label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1.5" data-testid="change-password-current" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">New password</label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="min. 6 characters" className="mt-1.5" data-testid="change-password-new" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Confirm new password</label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="change-password-confirm" />
        </div>
        <Button onClick={submit} disabled={saving} className="bg-navy hover:bg-navy-light text-white font-semibold" data-testid="change-password-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Change Password</>}
        </Button>
      </div>
    </div>
  );
}

export default function PortalProfile() {
  const { user, updateProfile, changeEmail, changePassword } = useUser();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-navy">Settings</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Update your profile photo, contact details, address, email and password.
        </p>
      </div>
      <ProfileSettingsCard user={user} role="Member" onSave={updateProfile} extended />
      <ChangeEmailCard user={user} changeEmail={changeEmail} />
      <ChangePasswordCard changePassword={changePassword} />
    </div>
  );
}
